import type {
  NonConformityRow,
  ProjectNonConformitiesView,
} from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';
import { NON_CONFORMITY_STATUS_LABEL } from '@obraplan/core/domain/quality/non-conformity';
import { formatBusinessDate } from '@/lib/format';
import type { XlsxCell, XlsxSheet } from '@/features/reports/xlsx';

/**
 * O FORM.30 — Controle de Não Conformidades — da obra, como a construtora preenche (RN-486).
 *
 * As dez colunas são as do formulário do cliente, na ordem dele (`FORM.30 - Controle de Não
 * conformidades.xlsx`, no Drive do SGQ): quem recebe a planilha cola as linhas na dele sem
 * remontar nada. O que o papel não tem e o sistema sabe vai **depois** da décima coluna — a
 * quantidade não conforme, a unidade e a situação —, e não no meio.
 *
 * Todas as NCs, canceladas inclusive, em ordem de número: a sequência do formulário não tem
 * buraco, e a cancelada diz por quê na "Correção".
 */
export const FORM_30_HEADERS = [
  'Número da Não Conformidade (Sequencial)',
  'Data da Identificação da Não Conformidade',
  'Empresa / Colaborador',
  'Apto',
  'Local',
  'Serviço',
  'Não-Conformidade Encontrada',
  'Fotos (Quando Necessário)',
  'Correção',
  'Data da Reinspeção e Aprovação',
  'Quantidade não conforme',
  'Unidade',
  'Situação',
] as const;

/** O dia de um instante gravado (ISO), como o papel escreve: `22/10/2026`. */
function diaDoInstante(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * O "Local": o rótulo da frente, com a torre na frente quando a obra tem mais de uma, e as
 * peças e os cômodos da NC depois — "ESTACAS — Subsolo · E76-A, E77-B", como o papel escreve
 * "P3A, P3B, P4A".
 */
function localDe(row: NonConformityRow, variasTorres: boolean): string {
  const frente =
    variasTorres && row.towerName !== undefined
      ? `${row.towerName} · ${row.localLabel}`
      : row.localLabel;
  return row.places.length === 0
    ? frente
    : `${frente} · ${row.places.map((place) => place.label).join(', ')}`;
}

/** O "Apto": os aptos dos cômodos da NC; sem cômodo, como o papel — "NA". */
function aptoDe(row: NonConformityRow): string {
  const aptos = [
    ...new Set(
      row.places.flatMap((place) =>
        place.positionLabel === undefined ? [] : [place.positionLabel],
      ),
    ),
  ].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
  return aptos.length === 0 ? 'NA' : aptos.join(', ');
}

/** A "Correção": a solução da resolvida, o motivo da cancelada, nada na aberta. */
export function correcaoDe(row: NonConformityRow): string {
  if (row.status === 'RESOLVED') return row.solution ?? '';
  if (row.status === 'CANCELLED') {
    return row.cancelReason === undefined ? 'Cancelada' : `Cancelada: ${row.cancelReason}`;
  }
  return '';
}

function quantidadeDe(row: NonConformityRow): XlsxCell {
  if (row.quantity === undefined) return '';
  const casas = row.unitDecimals ?? 6;
  return Number(row.quantity.toFixed(casas));
}

/** O "Responsável pela Inspeção": quem abriu as NCs, sem repetir. */
function responsaveis(rows: readonly NonConformityRow[]): string {
  return [...new Set(rows.map((row) => row.openedByName))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR'))
    .join(', ');
}

/** A aba da planilha, pronta para `toXlsx`. */
export function form30Sheet(view: ProjectNonConformitiesView): XlsxSheet {
  const rows = [...view.rows].sort((a, b) => a.number - b.number);
  const variasTorres =
    new Set(rows.flatMap((row) => (row.towerName === undefined ? [] : [row.towerName]))).size > 1;

  return {
    name: 'FORM.30',
    preamble: [
      ['Controle de Não Conformidade - Serviços'],
      [
        `Obra: ${view.projectName}`,
        '',
        '',
        '',
        '',
        '',
        `Responsável pela Inspeção: ${responsaveis(rows)}`,
      ],
    ],
    headers: [...FORM_30_HEADERS],
    rows: rows.map((row) => [
      row.number,
      formatBusinessDate(row.date),
      row.ownerName ?? '',
      aptoDe(row),
      localDe(row, variasTorres),
      row.serviceName,
      row.description,
      row.photoCount === 0
        ? '-'
        : `${String(row.photoCount)} ${row.photoCount === 1 ? 'foto' : 'fotos'} no ObraPlan`,
      correcaoDe(row),
      row.status === 'RESOLVED' && row.resolvedAt !== undefined
        ? diaDoInstante(row.resolvedAt)
        : '',
      quantidadeDe(row),
      row.quantity === undefined ? '' : (row.unitName ?? ''),
      NON_CONFORMITY_STATUS_LABEL[row.status],
    ]),
    widths: [14, 16, 22, 6, 30, 30, 50, 16, 50, 16, 14, 10, 12],
  };
}

/** `FORM.30 - Controle de Não Conformidades - Residencial Vienna.xlsx` */
export function form30FileName(projectName: string): string {
  const obra = projectName.replace(/[\\/:*?"<>|]/g, ' ').trim();
  return `FORM.30 - Controle de Não Conformidades${obra === '' ? '' : ` - ${obra}`}.xlsx`;
}
